import { Button, Icon, Loader } from 'semantic-ui-react'
import { gql, useMutation, useQuery } from '@apollo/client'

import Logo from '@components/JobsViewer/Logo'
import { createApplication } from '@src/graphql/mutations'
import { useAuth } from '@src/auth'
import { getJobSeekerIdForUser } from 'queries'
import Tag from '@components/JobsViewer/Tag'
import styles from './style.module.css'
import fromNow from '@src/helpers/fromNow'
import EmptyApplicationCard from '@components/EmptyApplicationCard'
import AppLink from '@components/JobsViewer/AppLink'
import CoverLetterButton from '@components/JobsViewer/CoverLetterButton'
import formatTimeAgo from '@src/helpers/formatTimeAgo'
import ResumeLink from '../JobsViewer/ResumeLink'
// import RoleDetailsSummary from '@components/JobsViewer/RoleDetailsSummary'
// import HybridSummary from '@components/JobsViewer/HybridSummary'
// import LocationSummary from '@components/JobsViewer/LocationSummary'
// import SponsorDetails from '@components/JobsViewer/SponsorDetails'
// import DigitalNomadBadge from '@components/JobsViewer/DigitalNomadBadge'
// import ShareButtonRow from '@components/ShareButtonRow'

enum FlaggedContent {
  JOBSEEKER = 'JOBSEEKER',
  JOBPOSTING = 'JOBPOSTING',
  COMPANY = 'COMPANY',
}

export default function TabletApplicationCard({
  post,
  status,
  active,
  applicationDate,
  onClick,
  empty,
  showApplicationDate,
  dashboardCard
}) {
  const { user } = useAuth()

  const [
    apply,
    { data: updateData, loading: applicationLoading, error, reset },
  ] = useMutation(gql(createApplication))

  const { data: userData, loading } = useQuery(gql(getJobSeekerIdForUser), {
    variables: { userId: user?.username },
  })

  const jobSeekerId = userData?.getUserById?.jobSeeker?.id

  const applyToJob = (e) => {
    e.stopPropagation()
    e.preventDefault()

    if (jobSeekerId) {
      const vars = {
        variables: {
          input: {
            id: (Math.random() * 1000).toString(),
            jobId: post?.id,
            jobSeekerId,
          },
        },
      }

      apply(vars)
    } else {
    }
  }

  if (empty) {
    return <EmptyApplicationCard />
  }

  function renderDatePosted(post) {
    let result = fromNow(new Date(post?.datePosted))
    return `Posted ${result}`
  }

  const getApplicationLabels = (postData, standardWidth) => {
    const labels = []

    const {
      startSalaryRangeThousands: startRange,
      endSalaryRangeThousands: endRange,
    } = postData

    if (startRange) {
      let salaryText = ''

      if (startRange && endRange) {
        salaryText = `$${startRange}k - $${endRange}k`
      } else {
        salaryText = `$${startRange}k`
      }

      labels.push(
        <Tag
          key={`${postData?.id}-parttime`}
          standardWidth={standardWidth}
          color="green"
          icon="dollar sign"
          text={salaryText}
          doNotTrim
        />,
      )
    }

    if (postData?.scrapedSource === 'yc') {
      labels.push(
        <Tag
          key={`${postData?.id}-yc`}
          standardWidth={standardWidth}
          color="teal"
          icon="YC"
          text={'YCombinator'}
        />,
      )
    }

    switch (postData?.roleType) {
      case 'PARTTIME':
        labels.push(
          <Tag
            key={`${postData?.id}-parttime`}
            standardWidth={standardWidth}
            color="teal"
            icon="hourglass"
            text={'Part Time'}
          />,
        )
        break
      case 'FULLTIME':
        labels.push(
          <Tag
            key={`${postData?.id}-fulltime`}
            standardWidth={standardWidth}
            color="teal"
            icon="hourglass"
            text={'Full Time'}
          />,
        )
        break
      case 'CONTRACT':
        labels.push(
          <Tag
            key={`${postData?.id}-contract`}
            standardWidth={standardWidth}
            color="teal"
            icon="hourglass"
            text={'Contract'}
          />,
        )
        break
      default:
        break
    }

    switch (postData?.remotePolicy) {
      case 'HYBRIDREMOTE':
        labels.push(
          <Tag
            key={`${postData?.id}-hybrid`}
            standardWidth={standardWidth}
            color="grey"
            icon="wifi"
            text="Hybrid Remote"
          />,
        )
        break
      case 'FULLREMOTE':
        labels.push(
          <Tag
            key={`${postData?.id}-fullremote`}
            standardWidth={standardWidth}
            color="grey"
            icon="wifi"
            text="100% Remote"
          />,
        )
        break
      case 'REMOTEOPTIONAL':
        labels.push(
          <Tag
            key={`${postData?.id}-optional`}
            standardWidth={standardWidth}
            color="grey"
            icon="wifi"
            text="Remote Optional"
          />,
        )
        break
      case 'ONSITE':
        labels.push(
          <Tag
            key={`${postData?.id}-onsite`}
            standardWidth={standardWidth}
            color="black"
            icon="building outline"
            text="Onsite"
          />,
        )
        break        
      default:
        break
    }

    if (postData?.officeLocations?.length) {
      if (postData?.officeLocations?.length > 1) {
        const locs = postData?.officeLocations
        labels.push(
          <Tag
            standardWidth={standardWidth}
            key={`${postData?.id}-loc`}
            color="orange"
            icon="map pin"
            text={`${locs.length} Locations`}
          />,
        )
      } else {
        labels.push(
          <Tag
            standardWidth={standardWidth}
            key={`${postData?.id}-${postData?.officeLocations[0]?.formatted_address}`}
            color="orange"
            icon="map pin"
            text={postData?.officeLocations[0]?.formatted_address}
          />,
        )
      }
    }

    return labels
  }

  const renderApplyNow = () => {
    if (post?.scrapedUrl) {
      return (
        <a target="_blank" href={post?.scrapedUrl} rel="noopener noreferrer">
          <Button
            circular
            color="blue"
            style={{ marginLeft: '8px', marginRight: '0' }}
          >
            Apply Now{' '}
            <Icon
              name="location arrow"
              style={{ marginLeft: '8px', marginRight: '0' }}
            />
          </Button>
        </a>
      )
    }
    if (status === 'ACCEPTED') {
      return (
        <div className={styles.accepted}>
          <b>Application Accepted</b>
        </div>
      )
    } else if (status === 'DECLINED') {
      return (
        <div className={styles.declined}>
          <b>Application Declined</b>
        </div>
      )
    }

    if (applied || applicationDate) {
      return (
        <Button circular disabled color="blue">
          Applied{' '}
          <Icon name="check" style={{ marginLeft: '8px', marginRight: '0' }} />
        </Button>
      )
    } else if (applicationLoading) {
      return (
        <Button circular color="blue" disabled onClick={applyToJob}>
          Apply Now{' '}
          <Loader
            size="mini"
            inverted
            active
            inline
            style={{ marginLeft: '8px', marginRight: '0' }}
          ></Loader>
        </Button>
      )
    }
    return (
      <Button circular color="blue" onClick={applyToJob}>
        Apply Now{' '}
        <Icon
          name="location arrow"
          style={{ marginLeft: '8px', marginRight: '0' }}
        />
      </Button>
    )
  }

  const {
    jobTitle,
    companyId,
    companyName,
    companyProfile,
    digitalNomadFriendly,
    willSponsor,
    applied,
  } = post

  return (
    <div className={`${styles.mobileCardContainer}`} onClick={onClick}>
      <div className={styles.textContainer}>
        <div className={styles.title}>{jobTitle}</div>
        <div className={styles.company}>{companyName}</div>
        <div className={styles.labelsContainer}>
          {getApplicationLabels(post, true)}
        </div>
        {applicationDate && (
          <div className={styles.details}>
            <div className={styles.applicationDate}>
              {`Applied ${formatTimeAgo(applicationDate)}`}
            </div>
            {
              dashboardCard && (
                <div className={styles.appLink}>
                  <AppLink dashboardCard={dashboardCard} post={post} />
                  <CoverLetterButton post={post} dashboardCard />
                  <ResumeLink post={post} dashboardCard />
                </div>
              )
            }
          </div>
        )}
      </div>

      <div className={styles.statsContainer}>
        <Logo
          image={post?.jobLogo}
          companyName={companyProfile?.name}
          companyId={companyId}
          active={active}
        />
        {willSponsor && (
          <div>
            <Icon name="check" color="green" />
            <span>Will Sponsor VISA</span>
          </div>
        )}
        <div className={styles.datePosted}>{renderDatePosted(post)}</div>
      </div>
    </div>
  )
}

function formatISOString(isoString) {
  // Parse ISO string into a Date object
  const date = new Date(isoString);

  // Array of month names
  const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
  ];

  // Get month, day, and year
  const month = months[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();

  // Format the date
  const formattedDate = `${month} ${day}, ${year}`;

  return formattedDate;
}
